import { Classes, Dialog, Spinner, Tab, Tabs } from "@blueprintjs/core";
import { Search } from "modules/common/components/form/Search";
import { ItemListPredicate } from "@blueprintjs/select";
import { observer } from "mobx-react";
import { getSnapshot } from "mobx-state-tree";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { DefaultSelectedOption, renderSingleOption, SimpleSelect } from "modules/common/services/form/select";
import React, { useEffect, useMemo, useState } from "react";
import { OneCEntityMappingSnapshotType, OneCMappingSnapshotType, OneCStoreType } from "../models/onec-store";
import styles from "./OneCImportDialog.module.scss";

export interface MappingRole {
  key: string;
  label: string;
}

// Пресеты ролей полей для сопоставления (структура организации).
export const DEPARTMENT_ROLES: MappingRole[] = [
  { key: "Name", label: "Наименование" },
  { key: "ExternalId", label: "Идентификатор 1С (Ref_Key)" },
  { key: "DeletionMark", label: "Признак удаления (архив)" },
];

export const POSITION_ROLES: MappingRole[] = DEPARTMENT_ROLES;

// Сотрудники — составная сущность; в настройках задаём минимум (остальное собирается ридером из связанных ресурсов).
export const EMPLOYEE_ROLES: MappingRole[] = [
  { key: "Name", label: "ФИО (представление)" },
  { key: "ExternalId", label: "Идентификатор 1С (Ref_Key)" },
  { key: "Archived", label: "Поле-флаг увольнения/архива" },
];

type EntityKey = "departments" | "positions" | "employees";

interface OneCImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  store: OneCStoreType;
  entityKey: EntityKey;
  title: string;
  roles: MappingRole[];
  onImported?: () => void;
}

const STATUS_LABEL: TStringMap<string> = {
  new: "Новое",
  linked: "Есть в приложении",
  candidate: "Совпадает по имени",
  onlyInRost: "Только в приложении",
};

const STATUS_CLASS: TStringMap<string> = {
  new: "statusNew",
  linked: "statusLinked",
  candidate: "statusCandidate",
  onlyInRost: "statusOnlyRost",
};

// Ограничиваем крупный список каталогов (в 1С их тысячи), чтобы поповер не тормозил.
const limitList = (max: number): ItemListPredicate<SelectItem> => (query, items) => {
  const q = query.toLowerCase().trim();
  const filtered = q ? items.filter((i) => i.label.toLowerCase().indexOf(q) >= 0) : items;
  return filtered.slice(0, max);
};
const limitResources = limitList(200);
const limitFields = limitList(200);
const limitBind = limitList(300);

const toItems = (names: string[]): SelectItem[] => names.map((n) => ({ id: n, label: n }));
const findItem = (items: SelectItem[], id: string | undefined) => items.find((i) => i.id === id) || null;

const OneCImportDialogBase: React.FC<OneCImportDialogProps> = ({
  isOpen,
  onClose,
  store,
  entityKey,
  title,
  roles,
  onImported,
}) => {
  const [tab, setTab] = useState<string>("settings");
  const [search, setSearch] = useState<string>("");
  const [resource, setResource] = useState<string>("");
  const [fieldMap, setFieldMap] = useState<TStringMap<string>>({});
  // Решения по строкам: ключ — externalId. include — импортировать; targetId — привязка ("" = создать);
  // positionTargetId — должность для сотрудника ("" = заглушка).
  const [decisions, setDecisions] = useState<
    TStringMap<{ include: boolean; targetId: string; positionTargetId: string }>
  >({});

  const isEmployees = entityKey === "employees";

  // Поле поиска в выпадающем списке привязки: список зависит от того, что импортируем
  const bindPlaceholder = isEmployees ? "Сотрудник" : entityKey === "positions" ? "Должность" : "Отдел";

  // При открытии — грузим каталоги и профиль сопоставления.
  useEffect(() => {
    if (isOpen) {
      setTab("settings");
      store.loadResources();
      store.loadMapping();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Когда профиль загружен — инициализируем каталог/поля. Если связи уже настроены (профиль сохранён) —
  // открываем сразу таблицу и грузим предпросмотр; иначе — вкладку «Настройки импорта».
  useEffect(() => {
    if (isOpen && store.mapping) {
      const entity = store.mapping[entityKey];
      setResource(entity.resource);
      setFieldMap({ ...entity.fieldMap });
      if (entity.resource) {
        store.loadFields(entity.resource);
      }
      if (store.mapping.saved && entity.resource) {
        setTab("preview");
        store.previewImport(entityKey);
      } else {
        setTab("settings");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entityKey, store.mapping]);

  // При получении предпросмотра — инициализируем решения: связанные/кандидаты предзаполнены привязкой,
  // новые — «создать» (targetId пустой). Строки «только в приложении» в импорте не участвуют.
  useEffect(() => {
    const p = store.preview;
    if (p) {
      const init: TStringMap<{ include: boolean; targetId: string; positionTargetId: string }> = {};
      p.items.forEach((it) => {
        if (it.status === "onlyInRost" || !it.externalId) {
          return;
        }
        init[it.externalId] = {
          include: true,
          targetId: it.targetId || "",
          positionTargetId: it.suggestedPositionId || "",
        };
      });
      setDecisions(init);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.preview]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resourceItems = useMemo(() => toItems(store.resources.slice()), [store.resources.length]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fieldItems = useMemo(() => toItems(store.fields.slice()), [store.fields.length]);

  // Варианты привязки строки: «прикрепить из приложения» (создать новую) + существующие записи приложения.
  // Для должностей в подписи показываем отдел, чтобы различать одноимённые.
  const bindItems: SelectItem[] = useMemo(() => {
    const base: SelectItem[] = [{ id: "", label: "Прикрепить из приложения" }];
    const p = store.preview;
    return p
      ? base.concat(p.rostEntities.map((e) => ({ id: e.id, label: e.group ? `${e.name} (${e.group})` : e.name })))
      : base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.preview]);

  // Подпись целевой записи приложения: в две строки — отдел (приглушённо) сверху, должность снизу.
  const renderTarget = (targetId: string | null, fallback: string | null) => {
    const entity = targetId && preview ? preview.rostEntities.find((e) => e.id === targetId) : null;
    if (!entity) {
      return <span className={styles.targetName}>{fallback || "—"}</span>;
    }
    return (
      <div className={styles.targetCell}>
        {entity.group && <div className={styles.targetGroup}>{entity.group}</div>}
        <div className={styles.targetName}>{entity.name}</div>
      </div>
    );
  };

  const emptyDecision = { include: false, targetId: "", positionTargetId: "" };

  const toggleInclude = (externalId: string) => {
    setDecisions((prev) => {
      const cur = prev[externalId] || emptyDecision;
      return { ...prev, [externalId]: { ...cur, include: !cur.include } };
    });
  };

  const setTarget = (externalId: string, targetId: string) => {
    setDecisions((prev) => {
      const cur = prev[externalId] || { ...emptyDecision, include: true };
      return { ...prev, [externalId]: { ...cur, targetId } };
    });
  };

  const setPosition = (externalId: string, positionTargetId: string) => {
    setDecisions((prev) => {
      const cur = prev[externalId] || { ...emptyDecision, include: true };
      return { ...prev, [externalId]: { ...cur, positionTargetId } };
    });
  };

  // Список должностей приложения для сотрудника: заглушка + существующие (с отделом в подписи).
  const positionItems: SelectItem[] = useMemo(() => {
    const base: SelectItem[] = [{ id: "", label: "Без должности (заглушка)" }];
    const p = store.preview;
    return p
      ? base.concat(p.positions.map((e) => ({ id: e.id, label: e.group ? `${e.name} (${e.group})` : e.name })))
      : base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.preview]);

  const onResourceSelect = (item: SelectItem) => {
    setResource(item.id);
    store.loadFields(item.id);
  };

  const onFieldSelect = (roleKey: string, item: SelectItem) => {
    setFieldMap((prev) => ({ ...prev, [roleKey]: item.id }));
  };

  const buildModel = (): OneCMappingSnapshotType | null => {
    if (!store.mapping) {
      return null;
    }

    const current = getSnapshot(store.mapping) as OneCMappingSnapshotType;
    const updatedEntity: OneCEntityMappingSnapshotType = {
      resource,
      fieldMap,
      relatedResources: current[entityKey].relatedResources,
    };

    return { ...current, [entityKey]: updatedEntity };
  };

  // Применить настройки импорта: сохраняем сопоставление, сравниваем 1С с приложением и переходим к таблице.
  const doApply = async () => {
    const model = buildModel();
    if (!model) {
      return;
    }
    const saved = await store.saveMapping(model);
    if (saved) {
      await store.previewImport(entityKey);
      setTab("preview");
    }
  };

  const applyDisabled = !resource || !fieldMap.Name || !fieldMap.ExternalId || !store.mapping || store.previewing;

  const doImport = async () => {
    const list = Object.keys(decisions)
      .filter((externalId) => decisions[externalId].include)
      .map((externalId) => ({
        externalId,
        targetId: decisions[externalId].targetId,
        positionTargetId: decisions[externalId].positionTargetId,
      }));

    if (list.length === 0) {
      return;
    }

    const ok = await store.applyImport(entityKey, list);
    if (ok) {
      onImported && onImported();
      await store.previewImport(entityKey);
    }
  };

  const selectedCount = Object.keys(decisions).filter((k) => decisions[k].include).length;

  const closeDialog = () => {
    store.clearPreview();
    setSearch("");
    onClose();
  };

  const notConfigured = !store.loadingResources && store.resources.length === 0;
  const canImport = entityKey === "departments" || entityKey === "positions" || entityKey === "employees";
  const preview = store.preview;

  // Записи приложения, уже занятые ручной привязкой, — их строки «только в приложении» скрываем.
  const usedTargetIds = new Set(
    Object.keys(decisions)
      .filter((k) => decisions[k].include && decisions[k].targetId)
      .map((k) => decisions[k].targetId)
  );

  // Базовый набор (без поиска) — по нему считаем сводку; поиск фильтрует только отображение.
  const baseItems = (preview ? preview.items : []).filter(
    (it) => !(it.status === "onlyInRost" && it.targetId && usedTargetIds.has(it.targetId))
  );

  const counts = { new: 0, linked: 0, candidate: 0, onlyInRost: 0 } as TStringMap<number>;
  baseItems.forEach((it) => {
    counts[it.status] = (counts[it.status] || 0) + 1;
  });

  const query = search.trim().toLowerCase();
  const visibleItems = query
    ? baseItems.filter((it) =>
        [it.sourceName, it.targetName, it.birthday, it.suggestedPositionName].some(
          (v) => (v || "").toLowerCase().indexOf(query) >= 0
        )
      )
    : baseItems;

  const mappingPanel = (
    <div className={styles.mappingPanel}>
      <div className={styles.label}>Каталог 1С</div>
      <SimpleSelect
        className={`full-width-select ${Classes.FILL}`}
        activeItem={findItem(resourceItems, resource)}
        items={resourceItems}
        itemRenderer={renderSingleOption}
        onItemSelect={onResourceSelect}
        popoverProps={{ rootBoundary: "viewport" }}
        inputProps={{ placeholder: "Каталог" }}
        filterable={true}
        itemListPredicate={limitResources}
      >
        <DefaultSelectedOption option={findItem(resourceItems, resource)} what="Выберите каталог" />
      </SimpleSelect>

      {resource && (
        <div className={styles.fields}>
          <div className={styles.label} style={{ marginTop: "16px" }}>
            Сопоставление полей
            {store.loadingFields && <Spinner size={16} className={styles.inlineSpinner} />}
          </div>

          {roles.map((role) => (
            <div key={role.key} className={styles.fieldRow}>
              <div className={styles.roleLabel}>{role.label}</div>
              <SimpleSelect
                className={`full-width-select ${Classes.FILL}`}
                activeItem={findItem(fieldItems, fieldMap[role.key])}
                items={fieldItems}
                itemRenderer={renderSingleOption}
                onItemSelect={(item: SelectItem) => onFieldSelect(role.key, item)}
                popoverProps={{ rootBoundary: "viewport" }}
                inputProps={{ placeholder: "Поле 1С" }}
                filterable={true}
                itemListPredicate={limitFields}
              >
                <DefaultSelectedOption option={findItem(fieldItems, fieldMap[role.key])} what="Не задано" />
              </SimpleSelect>
            </div>
          ))}
        </div>
      )}

      <div className={styles.applyRow}>
        <PlanrButton type="greenish" size="small" onClick={doApply} disabled={applyDisabled}>
          Применить
        </PlanrButton>
      </div>
    </div>
  );

  const previewPanel = (
    <div className={styles.previewPanel}>
      {store.previewing && (
        <div className={styles.center}>
          <Spinner size={20} /> <span>Сравнение с 1С…</span>
        </div>
      )}

      {!preview && !store.previewing && (
        <div className={styles.hint}>Нажмите «Предпросмотр», чтобы сравнить данные 1С с приложением.</div>
      )}

      {preview && !store.previewing && (
        <div className={styles.preview}>
          <div className={styles.summary}>
            <span className={styles.statusNew}>Новое: {counts.new}</span>
            <span className={styles.statusLinked}>Есть в приложении: {counts.linked}</span>
            <span className={styles.statusCandidate}>Совпадает по имени: {counts.candidate}</span>
            <span className={styles.statusOnlyRost}>Только в приложении: {counts.onlyInRost}</span>
          </div>
          <div className={styles.hint}>
            {isEmployees
              ? "Отметьте сотрудников для импорта. Должность подставляется из кадровой истории 1С; если её нет — выберите вручную или оставьте «Без должности». Рекомендуется сначала импортировать отделы и должности."
              : "Отметьте строки для импорта и при необходимости выберите отдел в приложении для привязки. Записи приложения, к которым вы привязались, скрываются из строк «Только в приложении»."}
          </div>
          <div className={styles.searchRow}>
            <Search query={search} onSearch={setSearch} placeholder="Поиск по данным…" style={{ marginLeft: 0 }} />
          </div>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: "40px" }} />
                  <th>{isEmployees ? "ФИО в 1С" : "Наименование в 1С"}</th>
                  {isEmployees && <th style={{ width: "110px" }}>Дата рожд.</th>}
                  <th style={{ width: "170px" }}>Статус</th>
                  <th style={{ width: "300px" }}>{isEmployees ? "Сотрудник в приложении" : "Привязать к записи в приложении"}</th>
                  {isEmployees && <th style={{ width: "300px" }}>Должность в приложении</th>}
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((item, idx) => {
                  const isImportable = item.status !== "onlyInRost" && !!item.externalId;
                  const decision = item.externalId ? decisions[item.externalId] : undefined;

                  return (
                    <tr
                      key={idx}
                      className={isImportable ? styles.rowClickable : styles.rowMuted}
                      onClick={isImportable ? () => toggleInclude(item.externalId as string) : undefined}
                    >
                      <td>
                        {isImportable && (
                          <input
                            type="checkbox"
                            checked={decision ? decision.include : false}
                            onChange={() => toggleInclude(item.externalId as string)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </td>
                      <td>
                        {item.sourceName || "—"}
                        {item.isDeleted && <span className={styles.deleted}> (удалён в 1С)</span>}
                      </td>
                      {isEmployees && <td>{item.birthday || "—"}</td>}
                      <td>
                        <span className={styles[STATUS_CLASS[item.status]]}>{STATUS_LABEL[item.status]}</span>
                      </td>
                      <td>
                        {item.status === "linked" ? (
                          renderTarget(item.targetId, item.targetName)
                        ) : isImportable ? (
                          <div onClick={(e) => e.stopPropagation()}>
                            <SimpleSelect
                              className={`full-width-select ${Classes.FILL}`}
                              activeItem={findItem(bindItems, decision ? decision.targetId : "")}
                              items={bindItems}
                              itemRenderer={renderSingleOption}
                              onItemSelect={(i: SelectItem) => setTarget(item.externalId as string, i.id)}
                              popoverProps={{ rootBoundary: "viewport" }}
                              inputProps={{ placeholder: bindPlaceholder }}
                              filterable={true}
                              itemListPredicate={limitBind}
                            >
                              <DefaultSelectedOption option={findItem(bindItems, decision ? decision.targetId : "")} />
                            </SimpleSelect>
                          </div>
                        ) : (
                          renderTarget(item.targetId, item.targetName)
                        )}
                      </td>
                      {isEmployees && (
                        <td>
                          {isImportable ? (
                            <div onClick={(e) => e.stopPropagation()}>
                              <SimpleSelect
                                className={`full-width-select ${Classes.FILL}`}
                                activeItem={findItem(positionItems, decision ? decision.positionTargetId : "")}
                                items={positionItems}
                                itemRenderer={renderSingleOption}
                                onItemSelect={(i: SelectItem) => setPosition(item.externalId as string, i.id)}
                                popoverProps={{ rootBoundary: "viewport" }}
                                inputProps={{ placeholder: "Должность" }}
                                filterable={true}
                                itemListPredicate={limitBind}
                              >
                                <DefaultSelectedOption
                                  option={findItem(positionItems, decision ? decision.positionTargetId : "")}
                                />
                              </SimpleSelect>
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Dialog
      title={title}
      isOpen={isOpen}
      onClose={closeDialog}
      backdropClassName="standard"
      className={`${styles.dialog} figma-dialog`}
      style={{ width: "1120px" }}
    >
      <div className={Classes.DIALOG_BODY}>
        {store.loadingResources && (
          <div className={styles.center}>
            <Spinner size={24} /> <span>Загрузка каталогов 1С…</span>
          </div>
        )}

        {notConfigured && (
          <div className={styles.warning}>
            Не удалось получить список каталогов 1С. Проверьте адрес, логин и пароль в разделе «Настройки → Интеграция с
            1С».
          </div>
        )}

        {!store.loadingResources && store.resources.length > 0 && (
          <Tabs id="onec-import-tabs" selectedTabId={tab} onChange={(t) => setTab(t as string)} large={true}>
            <Tab id="preview" title="Предпросмотр и импорт" panel={previewPanel} />
            <Tab id="settings" title="Настройки импорта" panel={mappingPanel} />
          </Tabs>
        )}
      </div>

      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <PlanrButton type="graybtn" size="small" onClick={closeDialog}>
            Отмена
          </PlanrButton>
          <PlanrButton
            type="greenish"
            size="small"
            onClick={doImport}
            disabled={!preview || !canImport || store.importing || selectedCount === 0}
            title={canImport ? undefined : "Импорт для этой сущности пока не поддерживается"}
          >
            {selectedCount > 0 ? `Импортировать (${selectedCount})` : "Импортировать"}
          </PlanrButton>
        </div>
      </div>
    </Dialog>
  );
};

export const OneCImportDialog = observer(OneCImportDialogBase);
